import { useEffect, useRef, useState } from "react";

import { Button } from "../button/Button";

import "./SegmentedControl.css";

import type { IconVariantProp } from "../icon/Icon";

export interface SegmentedControlChangeEvent {
  selected: number;
}

export interface SegmentedControlOption {
  label: string;
  icon: string;
  iconVariant?: IconVariantProp;
}

export interface SegmentedControlProps {
  selected: number | null;
  onSelectedChanged: ({ selected }: SegmentedControlChangeEvent) => void;
  options: SegmentedControlOption[];
}

export function SegmentedControl({
  selected,
  onSelectedChanged,
  options,
}: SegmentedControlProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [bgWidth, setBgWidth] = useState<number | null>(null);
  const [bgOffset, setBgOffset] = useState<number | null>(null);

  useEffect(() => {
    // Reset offset & width so they don't transition when a new button is selected later
    if (selected === null || selected === undefined) {
      setBgOffset(null);
      setBgWidth(null);
      return;
    }

    const button = ref.current?.querySelector(`[data-position="${selected}"]`);
    if (!button || !button.parentElement) {
      return;
    }

    const OUTLINE_PX = 1;
    const distanceFromLeft =
      button.getBoundingClientRect().left -
      button.parentElement.getBoundingClientRect().left -
      OUTLINE_PX;

    setBgOffset(distanceFromLeft);
    setBgWidth(button.getBoundingClientRect().width);
  }, [selected]);

  return (
    <div
      data-active={selected != null}
      className="SegmentedControl"
      style={{
        "--bg-offset": `${bgOffset}px`,
        "--bg-width": `${bgWidth}px`,
      }}
      ref={ref}
    >
      {options.map((option, index) => (
        <Button
          key={option.label}
          active={selected === index}
          icon={option.icon}
          iconVariant={option.iconVariant || "regular"}
          iconPosition="top"
          size="md"
          appearance="minimal"
          onClick={() => onSelectedChanged?.({ selected: index })}
          data-position={index}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
